import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';
import {
  createTextReveal,
  createRevealScheduler,
  createOpacityWriter,
  segmentOpacity,
  EDGE_SEGMENTS,
} from '@theme/text-reveal';

const MAX_SCROLL_VH = 300;

class ScrollReadingText extends Component {
  #blocks = [];
  #extraElements = [];
  #scrollHeightVh = 0;
  #totalLetters = 0;
  #scheduler = null;
  #lastOffscreenState = null;
  #lastScrollY = null;
  // Writes opacity for the extra elements; each block's own reveal handles its letters.
  #writeOpacity = createOpacityWriter();

  // Layout cache — computed once at init and on resize, never inside #update()
  #vh = 0;
  #sectionTopPx = 0;
  #sectionBottomPx = 0;
  #scrollRangePx = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  // Every cached value here is derived from the blocks, so a re-render has to rebuild all of it.
  updatedCallback() {
    super.updatedCallback?.();
    this.#teardown();
    this.#setup();
  }

  // Reduced motion gets no reveal and no scroll range either — the letters are never split, so the
  // text renders plainly and the section costs its own screen and nothing more.
  #setup() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.setProperty('--scroll-reading-text-height', '0px');
      return;
    }

    this.#lastOffscreenState = null;
    this.#lastScrollY = null;
    this.#splitTextIntoBlocks();
    this.#setScrollHeightCssVar();
    this.#cacheLayout();
    this.#scheduler = createRevealScheduler(this, {
      onMeasure: this.#onResize,
      onFrame: this.#update,
      gateOnVisibility: true,
    });
    this.#scheduler.start(this.abortController.signal);
    this.#syncInitialState();
  }

  #teardown() {
    this.#scheduler?.stop();
    this.#scheduler = null;
  }

  // vh, not px off innerHeight: iOS slides its toolbar away as you scroll down and back in as you
  // scroll up, and each move fires resize with a different innerHeight. A px height recomputed from
  // that re-lengths the page mid-scroll and shoves everything below the section. CSS vh is the large
  // viewport, which the toolbar cannot touch, so the section keeps one length for the whole scroll.
  #setScrollHeightCssVar() {
    this.style.setProperty('--scroll-reading-text-height', `${this.#scrollHeightVh}vh`);
  }

  // Caches every layout-dependent value, at init and on resize only, so #update() never reflows.
  #cacheLayout() {
    this.#vh = window.innerHeight;
    const rect = this.getBoundingClientRect();
    this.#sectionTopPx = rect.top + window.scrollY;
    this.#sectionBottomPx = this.#sectionTopPx + rect.height;
    this.#scrollRangePx = (this.#scrollHeightVh / 100) * this.#vh;

    const pxPerVh = this.#vh / 100;
    this.#blocks.forEach((block) => {
      block.startPx = block.blockScrollStartVh * pxPerVh;
      block.rangePx = (block.blockScrollEndVh - block.blockScrollStartVh) * pxPerVh;
    });
  }

  #splitTextIntoBlocks() {
    const readingTextBlocks = this.querySelectorAll('editorial-text');
    this.#blocks = [];
    this.#scrollHeightVh = 0;
    let accumulatedScrollVh = 0;

    readingTextBlocks.forEach((readingTextBlock) => {
      const block = this.#createBlockFromElement(readingTextBlock, accumulatedScrollVh);
      if (!block) return;
      this.#blocks.push(block);
      const blockScrollRangeVh = block.blockScrollEndVh - block.blockScrollStartVh;
      accumulatedScrollVh += blockScrollRangeVh;
      this.#scrollHeightVh += blockScrollRangeVh;
    });

    this.#capScrollHeight();
    this.#collectExtraElements();
  }

  // Speed times letter count has no ceiling of its own, and neither setting tells the merchant it
  // multiplies by how much they wrote. Past the cap the letters simply light faster: nobody reads
  // the section in letters per screen, they only feel how long they were held there.
  #capScrollHeight() {
    if (this.#scrollHeightVh <= MAX_SCROLL_VH) return;

    const scale = MAX_SCROLL_VH / this.#scrollHeightVh;
    this.#blocks.forEach((block) => {
      block.blockScrollStartVh *= scale;
      block.blockScrollEndVh *= scale;
    });
    this.#scrollHeightVh = MAX_SCROLL_VH;
  }

  #createBlockFromElement(readingTextBlock, blockScrollStartVh) {
    if (!readingTextBlock.textContent.trim()) return null;

    const speed = parseFloat(readingTextBlock.dataset.speed) || 1.5;
    const startOpacity = parseFloat(readingTextBlock.dataset.startOpacity) || 0;

    const reveal = createTextReveal(readingTextBlock, { startOpacity });
    const blockScrollRangeVh = speed * reveal.segmentCount;

    return {
      reveal,
      element: readingTextBlock,
      startOpacity,
      blockScrollStartVh,
      blockScrollEndVh: blockScrollStartVh + blockScrollRangeVh,
      startPx: 0,  // set in #cacheLayout
      rangePx: 0,  // set in #cacheLayout
    };
  }

  #collectExtraElements() {
    this.#totalLetters = this.#blocks.reduce((sum, block) => sum + block.reveal.segmentCount, 0);
    const firstStartOpacity = this.#blocks[0]?.startOpacity ?? 0;
    this.#extraElements = [];

    let letterIndex = 0;
    let hasSeenReadingText = false;
    const content = this.querySelector('.section-content') ?? this;

    content.querySelectorAll(':scope > *').forEach((child) => {
      const extraElement = child.matches('.subheading-typography, .button-block')
        ? child
        : child.querySelector('.subheading-typography, .button-block');
      const editorialText = child.querySelector('editorial-text');

      if (extraElement) {
        this.#extraElements.push({
          element: extraElement,
          index: letterIndex,
          revealOnVisible: !hasSeenReadingText,
          startOpacity: firstStartOpacity
        });
      }

      if (editorialText) {
        hasSeenReadingText = true;
        letterIndex += this.#blocks.find((block) => block.element === editorialText)?.reveal.segmentCount ?? 0;
      }
    });
  }

  #onResize = () => {
    this.#setScrollHeightCssVar();
    this.#cacheLayout();
    this.#update(true);
  };

  // Paints the dim itself rather than leaving it to CSS, which cannot reach into the letters' shadow.
  #syncInitialState() {
    if (this.#sectionTopPx > window.scrollY + this.#vh) {
      this.#lastOffscreenState = 'hidden';
      this.#setAllLettersHidden();
      return;
    }
    this.#update();
  }

  #setAllLettersRevealed() {
    this.#blocks.forEach(({ reveal }) => reveal.paint(1));
    this.#extraElements.forEach(({ element }) => this.#writeOpacity(element, 1));
  }

  // Resets the letters too, since a jump over the section never passes through the band.
  #setAllLettersHidden() {
    this.#blocks.forEach(({ reveal }) => reveal.paint(0));
    this.#hideExtras();
  }

  #hideExtras() {
    this.#extraElements.forEach(({ element, startOpacity }) => {
      this.#writeOpacity(element, startOpacity);
    });
  }

  // Hot path: cached layout plus scrollY only, and no frame the page did not move through.
  #update = (force = false) => {
    const scrollY = window.scrollY;
    if (!force && scrollY === this.#lastScrollY) return;
    this.#lastScrollY = scrollY;

    if (scrollY > this.#sectionBottomPx) {
      if (this.#lastOffscreenState !== 'revealed') {
        this.#lastOffscreenState = 'revealed';
        this.#setAllLettersRevealed();
      }
      return;
    }
    if (this.#sectionTopPx > scrollY + this.#vh) {
      if (this.#lastOffscreenState !== 'hidden') {
        this.#lastOffscreenState = 'hidden';
        this.#setAllLettersHidden();
      }
      return;
    }
    this.#lastOffscreenState = null;

    const scrolledPx = clamp(scrollY - this.#sectionTopPx, 0, this.#scrollRangePx);
    const maxLp = this.#totalLetters + EDGE_SEGMENTS;
    const globalLp = this.#scrollRangePx > 0
      ? (scrolledPx / this.#scrollRangePx) * maxLp
      : maxLp;

    this.#blocks.forEach((block) => {
      const progress = block.rangePx > 0
        ? clamp((scrolledPx - block.startPx) / block.rangePx, 0, 1)
        : 1;
      block.reveal.paint(progress);
    });

    this.#extraElements.forEach(({ element, index, startOpacity, revealOnVisible }) => {
      this.#writeOpacity(element, revealOnVisible
        ? 1
        : segmentOpacity(globalLp, index, startOpacity));
    });
  };
}

if (!customElements.get('scroll-reading-text')) {
  customElements.define('scroll-reading-text', ScrollReadingText);
}
